import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Copy,
  Edit,
  Eye,
  Mail,
  Plus,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Input, TextArea } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  category: string;
  description?: string;
  htmlContent: string;
  textContent: string;
  variables: string[];
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  lastUsed?: string;
  usageCount: number;
}

interface TemplateVariable {
  name: string;
  description: string;
  example: string;
  required: boolean;
}

const EmailTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editor state
  const [templateData, setTemplateData] = useState({
    name: '',
    subject: '',
    category: '',
    description: '',
    htmlContent: '',
    textContent: '',
    variables: [] as string[],
  });

  const categories = [
    { value: 'user-management', label: 'User Management' },
    { value: 'project-notifications', label: 'Project Notifications' },
    { value: 'invitations', label: 'Invitations' },
    { value: 'alerts', label: 'Alerts & Reminders' },
    { value: 'reports', label: 'Reports' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'system', label: 'System Messages' },
  ];

  const availableVariables: TemplateVariable[] = [
    {
      name: '{{user.firstName}}',
      description: "User's first name",
      example: 'John',
      required: false,
    },
    { name: '{{user.lastName}}', description: "User's last name", example: 'Doe', required: false },
    {
      name: '{{user.email}}',
      description: "User's email address",
      example: 'john@example.com',
      required: false,
    },
    {
      name: '{{project.name}}',
      description: 'Project name',
      example: 'Downtown Office Building',
      required: false,
    },
    { name: '{{project.code}}', description: 'Project code', example: 'DOB-2024', required: false },
    {
      name: '{{company.name}}',
      description: 'Company name',
      example: 'builderbel Construction',
      required: false,
    },
    {
      name: '{{system.url}}',
      description: 'System URL',
      example: 'https://app.builderbel.com',
      required: false,
    },
    {
      name: '{{date.current}}',
      description: 'Current date',
      example: '2024-01-15',
      required: false,
    },
    {
      name: '{{invitation.link}}',
      description: 'Invitation link',
      example: 'https://app.builderbel.com/invite/abc123',
      required: false,
    },
    {
      name: '{{reset.link}}',
      description: 'Password reset link',
      example: 'https://app.builderbel.com/reset/xyz789',
      required: false,
    },
  ];

  const sampleTemplates: EmailTemplate[] = [
    {
      id: '1',
      name: 'Welcome New User',
      subject: 'Welcome to {{company.name}} - Get Started Today!',
      category: 'user-management',
      description: 'Welcome email for new users joining the platform',
      htmlContent: `
        <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
          <div style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); padding: 40px 20px; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 28px;">Welcome to {{company.name}}!</h1>
            <p style="color: white; margin: 10px 0 0; font-size: 16px;">Your construction management journey starts here</p>
          </div>

          <div style="padding: 40px 20px; background: white;">
            <p style="font-size: 18px; color: #333; margin: 0 0 20px;">Hi {{user.firstName}},</p>

            <p style="font-size: 16px; color: #666; line-height: 1.6; margin: 0 0 20px;">
              We're excited to have you join our construction management platform! You now have access to powerful tools
              that will help streamline your project management workflow.
            </p>

            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: #f97316; margin: 0 0 15px; font-size: 18px;">Getting Started:</h3>
              <ul style="color: #666; margin: 0; padding-left: 20px; line-height: 1.8;">
                <li>Complete your profile setup</li>
                <li>Explore the project dashboard</li>
                <li>Connect with your team members</li>
                <li>Create your first project</li>
              </ul>
            </div>

            <div style="text-align: center; margin: 30px 0;">
              <a href="{{system.url}}" style="background: #f97316; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                Access Your Dashboard
              </a>
            </div>

            <p style="font-size: 14px; color: #999; margin: 30px 0 0; border-top: 1px solid #eee; padding-top: 20px;">
              If you have any questions, our support team is here to help. Just reply to this email or visit our help center.
            </p>
          </div>
        </div>
      `,
      textContent: `Welcome to {{company.name}}!

Hi {{user.firstName}},

We're excited to have you join our construction management platform! You now have access to powerful tools that will help streamline your project management workflow.

Getting Started:
• Complete your profile setup
• Explore the project dashboard
• Connect with your team members
• Create your first project

Access your dashboard: {{system.url}}

If you have any questions, our support team is here to help.`,
      variables: ['{{user.firstName}}', '{{company.name}}', '{{system.url}}'],
      isActive: true,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 156,
    },
    {
      id: '2',
      name: 'Project Invitation',
      subject: "You've been invited to join {{project.name}}",
      category: 'invitations',
      description: 'Invitation email for team members to join a project',
      htmlContent: `
        <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
          <div style="background: #1f2937; padding: 30px 20px; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px;">Project Invitation</h1>
          </div>

          <div style="padding: 30px 20px; background: white;">
            <p style="font-size: 18px; color: #333; margin: 0 0 20px;">Hi {{user.firstName}},</p>

            <p style="font-size: 16px; color: #666; line-height: 1.6; margin: 0 0 20px;">
              You've been invited to collaborate on the project <strong>{{project.name}}</strong> ({{project.code}}).
            </p>

            <div style="background: #f1f5f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: #475569; margin: 0 0 10px;">Project Details:</h3>
              <p style="color: #64748b; margin: 0; line-height: 1.6;">
                Project: {{project.name}}<br>
                Code: {{project.code}}<br>
                Date: {{date.current}}
              </p>
            </div>

            <div style="text-align: center; margin: 30px 0;">
              <a href="{{invitation.link}}" style="background: #3b82f6; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                Accept Invitation
              </a>
            </div>
          </div>
        </div>
      `,
      textContent: `Project Invitation

Hi {{user.firstName}},

You've been invited to collaborate on the project {{project.name}} ({{project.code}}).

Accept your invitation: {{invitation.link}}`,
      variables: [
        '{{user.firstName}}',
        '{{project.name}}',
        '{{project.code}}',
        '{{date.current}}',
        '{{invitation.link}}',
      ],
      isActive: true,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 89,
    },
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      // In production, this would fetch from API
      setTemplates(sampleTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setMessage({ type: 'error', text: 'Failed to load email templates' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditMode('create');
    setTemplateData({
      name: '',
      subject: '',
      category: '',
      description: '',
      htmlContent: '',
      textContent: '',
      variables: [],
    });
    setShowEditor(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditMode('edit');
    setSelectedTemplate(template);
    setTemplateData({
      name: template.name,
      subject: template.subject,
      category: template.category,
      description: template.description || '',
      htmlContent: template.htmlContent,
      textContent: template.textContent,
      variables: template.variables,
    });
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    try {
      // Validate template data
      if (!templateData.name || !templateData.subject || !templateData.category) {
        setMessage({ type: 'error', text: 'Please fill in all required fields' });
        return;
      }

      // Extract variables from content
      const htmlVariables = extractVariables(templateData.htmlContent);
      const textVariables = extractVariables(templateData.textContent);
      const subjectVariables = extractVariables(templateData.subject);
      const allVariables = [...new Set([...htmlVariables, ...textVariables, ...subjectVariables])];

      const templateToSave = {
        ...templateData,
        variables: allVariables,
        id: editMode === 'create' ? Date.now().toString() : selectedTemplate!.id,
        isActive: true,
        isSystem: false,
        createdAt: editMode === 'create' ? new Date().toISOString() : selectedTemplate!.createdAt,
        updatedAt: new Date().toISOString(),
        usageCount: editMode === 'create' ? 0 : selectedTemplate!.usageCount,
      };

      if (editMode === 'create') {
        setTemplates(prev => [...prev, templateToSave as EmailTemplate]);
      } else {
        setTemplates(prev =>
          prev.map(t => (t.id === templateToSave.id ? (templateToSave as EmailTemplate) : t))
        );
      }

      setShowEditor(false);
      setMessage({
        type: 'success',
        text: `Template ${editMode === 'create' ? 'created' : 'updated'} successfully!`,
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: 'Failed to save template' });
    }
  };

  const handleDeleteTemplate = async (template: EmailTemplate) => {
    if (template.isSystem) {
      setMessage({ type: 'error', text: 'Cannot delete system templates' });
      return;
    }

    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      setMessage({ type: 'success', text: 'Template deleted successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting template:', error);
      setMessage({ type: 'error', text: 'Failed to delete template' });
    }
  };

  const handleDuplicateTemplate = (template: EmailTemplate) => {
    const duplicated = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (Copy)`,
      isSystem: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0,
    };
    setTemplates(prev => [...prev, duplicated]);
    setMessage({ type: 'success', text: 'Template duplicated successfully' });
    setTimeout(() => setMessage(null), 3000);
  };

  const extractVariables = (content: string): string[] => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const matches = content.match(variableRegex);
    return matches ? [...new Set(matches)] : [];
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('htmlContent') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setTemplateData(prev => ({ ...prev, htmlContent: newText }));

      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  if (showEditor) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        {/* Editor Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowEditor(false)}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {editMode === 'create' ? 'Create' : 'Edit'} Email Template
              </h1>
              <p className="text-sm text-gray-600">
                Design and configure email templates with variables and styling
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={handleSaveTemplate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Template
            </button>
          </div>
        </div>

        {/* Status Message */}
        {message && (
          <div
            className={`rounded-lg p-4 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-center">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 mr-2" />
              )}
              {message.text}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Template Name"
                  value={templateData.name}
                  onChange={e => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Welcome Email"
                  required
                />

                <Dropdown
                  label="Category"
                  options={categories}
                  value={templateData.category}
                  onChange={value => setTemplateData(prev => ({ ...prev, category: value }))}
                  placeholder="Select category"
                  required
                />
              </div>

              <Input
                label="Email Subject"
                value={templateData.subject}
                onChange={e => setTemplateData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Welcome to {{company.name}}!"
                hint="You can use variables like {{user.firstName}} in the subject"
                required
              />

              <TextArea
                label="Description"
                value={templateData.description}
                onChange={value => setTemplateData(prev => ({ ...prev, description: value }))}
                placeholder="Brief description of when this template is used..."
                rows={3}
              />
            </div>

            {/* Content Editor */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Content</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HTML Content
                  </label>
                  <textarea
                    id="htmlContent"
                    value={templateData.htmlContent}
                    onChange={e =>
                      setTemplateData(prev => ({ ...prev, htmlContent: e.target.value }))
                    }
                    className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400 font-mono text-sm"
                    placeholder="Enter HTML content here..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plain Text Content
                  </label>
                  <textarea
                    value={templateData.textContent}
                    onChange={e =>
                      setTemplateData(prev => ({ ...prev, textContent: e.target.value }))
                    }
                    className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400"
                    placeholder="Enter plain text version here..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Available Variables */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Variables</h3>
              <div className="space-y-2">
                {availableVariables.map(variable => (
                  <div key={variable.name} className="group">
                    <button
                      onClick={() => insertVariable(variable.name)}
                      className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="font-mono text-sm text-red-500">{variable.name}</div>
                      <div className="text-xs text-gray-600">{variable.description}</div>
                      <div className="text-xs text-gray-400">Example: {variable.example}</div>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            {showPreview && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="text-sm text-gray-600 mb-2">Subject:</div>
                  <div className="font-medium mb-4">{templateData.subject || 'No subject'}</div>
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: templateData.htmlContent || '<p>No content</p>',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard/settings')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Email Templates</h1>
            <p className="text-sm text-gray-600">
              Create and manage email templates for notifications, invitations, and communications
            </p>
          </div>
        </div>

        <button
          onClick={handleCreateTemplate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`rounded-lg p-4 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400"
            />
          </div>

          <Dropdown
            label=""
            placeholder="All Categories"
            options={[{ value: '', label: 'All Categories' }, ...categories]}
            value={selectedCategory}
            onChange={setSelectedCategory}
          />

          <div className="flex items-end">
            <span className="text-sm text-gray-600">
              {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                    {template.isSystem && (
                      <span className="px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 rounded-full">
                        System
                      </span>
                    )}
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        template.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-2">{template.subject}</p>

                  {template.description && (
                    <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                  )}

                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>
                      Category: {categories.find(c => c.value === template.category)?.label}
                    </span>
                    <span>Used: {template.usageCount} times</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditTemplate(template)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => handleDuplicateTemplate(template)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </button>

                  {!template.isSystem && (
                    <button
                      onClick={() => handleDeleteTemplate(template)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="text-xs text-gray-500">
                  {template.variables.length} variable{template.variables.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedCategory
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first email template.'}
            </p>
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplates;
